import { Graphics, Sprite, type Renderer as PixiRenderer } from 'pixi.js';
import type { BuildingType } from '../sim/types';
import type { GrayboxTextures } from '../render/graybox';
import { DESIGNATION_TINT, ROAD_TINT } from '../render/tint';

export type GlyphKind =
  | 'mine'
  | 'cancel'
  | 'housing'
  | 'town'
  | 'civic'
  | 'utilities'
  | 'industry'
  | 'demolish'
  | 'road'
  | 'pipe'
  | 'cable'
  | 'paved'
  | 'reclaim'
  | 'cog'
  | 'paper';

/**
 * Renders the game's actual textures (and a few drawn glyphs) into DOM
 * canvases for the sidebar, so UI imagery always matches the world art —
 * including after any future sprite swap.
 */
export class IconFactory {
  private readonly renderer: PixiRenderer;
  private readonly tex: GrayboxTextures;
  private readonly cache = new Map<string, HTMLCanvasElement>();

  constructor(renderer: PixiRenderer, tex: GrayboxTextures) {
    this.renderer = renderer;
    this.tex = tex;
  }

  private extract(target: Sprite | Graphics, key: string): HTMLCanvasElement {
    let source = this.cache.get(key);
    if (!source) {
      source = this.renderer.extract.canvas(target) as HTMLCanvasElement;
      this.cache.set(key, source);
    }
    target.destroy();
    // cloneNode does not copy canvas pixels — blit into a fresh canvas so the
    // same icon can appear in several places.
    const copy = document.createElement('canvas');
    copy.width = source.width;
    copy.height = source.height;
    copy.getContext('2d')!.drawImage(source, 0, 0);
    copy.className = 'pixel-icon';
    return copy;
  }

  buildingIcon(type: BuildingType): HTMLCanvasElement {
    const sprite = new Sprite(this.tex.building(type).texture);
    return this.extract(sprite, `building:${type}`);
  }

  glyph(kind: GlyphKind): HTMLCanvasElement {
    if (kind === 'road') {
      const sprite = new Sprite(this.tex.road);
      sprite.tint = ROAD_TINT;
      return this.extract(sprite, 'glyph:road');
    }
    if (kind === 'pipe') {
      const sprite = new Sprite(this.tex.pipe);
      return this.extract(sprite, 'glyph:pipe');
    }
    if (kind === 'cable') {
      // Yellow twin of the pipe run.
      const sprite = new Sprite(this.tex.pipe);
      sprite.tint = 0xe8c95a;
      return this.extract(sprite, 'glyph:cable');
    }
    if (kind === 'paved') {
      const sprite = new Sprite(this.tex.road);
      sprite.tint = 0xa8a8a0;
      return this.extract(sprite, 'glyph:paved');
    }
    const g = new Graphics();
    switch (kind) {
      case 'mine':
        // Pickaxe: diagonal handle + curved head approximated by a chevron.
        g.poly([10, 26, 13, 23, 26, 10, 23, 7]).fill(0x8a6a3a);
        g.poly([6, 12, 14, 4, 16, 6, 10, 12, 16, 18, 14, 20]).fill(0xb8b2a8);
        break;
      case 'cancel':
        g.poly([14, 2, 26, 8, 14, 14, 2, 8]).fill({ color: DESIGNATION_TINT, alpha: 0.9 });
        g.moveTo(8, 4).lineTo(20, 12).stroke({ width: 3, color: 0xe05252 });
        g.moveTo(20, 4).lineTo(8, 12).stroke({ width: 3, color: 0xe05252 });
        break;
      case 'housing':
        g.poly([14, 3, 26, 12, 22, 12, 22, 24, 6, 24, 6, 12, 2, 12]).fill(0xd9c9a8);
        g.rect(11, 16, 6, 8).fill({ color: 0x000000, alpha: 0.4 });
        break;
      case 'town':
        // Beer mug.
        g.rect(6, 6, 13, 18).fill(0xe8c170);
        g.rect(6, 6, 13, 5).fill(0xf5efe0);
        g.rect(19, 10, 5, 9).stroke({ width: 3, color: 0xe8c170 });
        break;
      case 'civic':
        // Schoolhouse bell.
        g.poly([14, 4, 18, 6, 20, 14, 24, 18, 4, 18, 8, 14, 10, 6]).fill(0xe8c170);
        g.rect(12, 2, 4, 3).fill(0x8a6a3a);
        g.circle(14, 21, 2.2).fill(0xe8c170);
        break;
      case 'utilities':
        g.poly([16, 2, 8, 15, 14, 15, 10, 26, 22, 12, 15, 12]).fill(0xf2c14e);
        break;
      case 'industry':
        // Ore truck: cab + bed + wheels.
        g.rect(3, 12, 10, 8).fill(0x9a8468);
        g.rect(13, 8, 12, 12).fill(0x6f6f78);
        g.circle(8, 22, 3).fill(0x3a2f28);
        g.circle(20, 22, 3).fill(0x3a2f28);
        break;
      case 'demolish':
        // Sledgehammer.
        g.poly([12, 26, 15, 23, 24, 8, 21, 5]).fill(0x8a6a3a);
        g.rect(4, 3, 14, 8).fill(0xb8b2a8);
        break;
      case 'reclaim':
        // Shovel planted in a filled mound with a sprout on top.
        g.poly([2, 24, 14, 18, 26, 24, 26, 27, 2, 27]).fill(0x9a7a52);
        g.poly([6, 24, 14, 20, 22, 24]).fill({ color: 0x8a6a44, alpha: 0.7 });
        g.poly([19, 4, 22, 7, 14, 17, 11, 14]).fill(0x8a6a3a);
        g.poly([8, 14, 14, 20, 10, 23, 5, 17]).fill(0xb8b2a8);
        g.moveTo(9, 18).lineTo(6, 12).stroke({ width: 2, color: 0x5f7d43 });
        g.circle(5, 11, 2).fill(0x6f9150);
        g.circle(8, 10, 1.6).fill(0x5f7d43);
        break;
      case 'cog': {
        // Settings gear: eight square teeth around a ring, punched center.
        for (let index = 0; index < 8; index += 1) {
          const angle = (index / 8) * Math.PI * 2;
          const cx = 14 + Math.cos(angle) * 10;
          const cy = 14 + Math.sin(angle) * 10;
          g.rect(cx - 2.5, cy - 2.5, 5, 5).fill(0xcbbd96);
        }
        g.circle(14, 14, 8.5).fill(0xcbbd96);
        g.circle(14, 14, 3.5).fill(0x1a1410);
        break;
      }
      case 'paper':
        // Sheet of paper with a folded corner and text lines.
        g.poly([5, 2, 18, 2, 23, 7, 23, 26, 5, 26]).fill(0xf5efe0);
        g.poly([18, 2, 18, 7, 23, 7]).fill(0xd8c9a4);
        for (const y of [11, 15, 19]) {
          g.rect(8, y, 12, 2).fill(0x8a7a5c);
        }
        g.rect(8, 23, 7, 2).fill(0x8a7a5c);
        break;
    }
    return this.extract(g, `glyph:${kind}`);
  }
}
